// =====================================================================
// commande-prete/clients.js — Commandes prêtes v2 multi-client (16-10-2026)
// =====================================================================
// Champ « Client » avec propositions (SPEC-v2 §1.2 et §5) — logique PURE du
// composant ChoixClient.jsx (encodage, liste, chargement) :
//   - normalisation SANS accents ni casse (« Volt » trouve « Voltère »), espaces
//     regroupés : la saisie et les noms sont comparés sur cette forme ;
//   - sous-chaîne : « par » trouve « Nespart » ; ordre des propositions = nom égal,
//     puis début du nom, puis début d'un mot, puis le reste (alphabétique) ;
//   - surlignage de la partie trouvée, dans le nom TEL QU'IL EST ÉCRIT (accents compris) ;
//   - Entrée = la proposition mise en avant (la 1re par défaut) ;
//   - choix automatique UNIQUEMENT quand la liste s'est réduite à UNE proposition
//     ÉGALE à la saisie (« la liste se réduit jusqu'à la correspondance exacte ») :
//     taper « Yashi » quand « Yashi Pro » existe ne choisit rien tout seul (sinon le
//     focus partirait à la réf. pendant qu'on tape « Pro »).
// « Commande de test » (17-10-2026) : la case n'est proposée qu'aux administrateurs (textes plus bas).
// Fonctions PURES (aucun DOM) : testées par frontend/test/commande-prete.test.mjs.
// =====================================================================

export const MSG_AUCUN_CLIENT = 'Aucun client ne correspond — vérifiez l\'orthographe';
export const MSG_TAPER_CLIENT = 'Tapez quelques lettres du nom du client, ou touchez-le dans la liste';
export const MSG_CLIENT_NON_CHOISI = 'Client non choisi : touchez-le dans la liste ou appuyez sur Entrée';
export const MAX_PROPOSITIONS = 8;

const MARQUES = /[̀-ͯ]/g;

// Forme normalisée d'un texte + table de correspondance : carte[j] = [début, fin[ dans le texte
// ORIGINAL du caractère normalisé j (un « é » reste un seul caractère, une ligature peut en donner
// plusieurs). Espaces de tête/fin retirés, suites d'espaces réduites à une.
export function carteNorm(texte) {
  const s = String(texte ?? '');
  let norm = '';
  const carte = [];
  let pos = 0;
  let espaceAvant = true;   // retire les espaces de tête et regroupe les suites
  for (const ch of s) {
    let n = ch.normalize('NFD').replace(MARQUES, '').toLowerCase();
    if (/^\s+$/.test(n)) n = ' ';
    for (const c of n) {
      if (c === ' ' && espaceAvant) continue;
      norm += c;
      for (let i = 0; i < c.length; i++) carte.push([pos, pos + ch.length]);
      espaceAvant = c === ' ';
    }
    pos += ch.length;
  }
  if (norm.endsWith(' ')) { norm = norm.slice(0, -1); carte.pop(); }
  return { norm, carte };
}
export const normClient = (texte) => carteNorm(texte).norm;

// Rang d'un nom pour une saisie (plus petit = plus pertinent) ; null = ne correspond pas.
function rang(nomNorm, q) {
  if (!q) return 3;
  const k = nomNorm.indexOf(q);
  if (k < 0) return null;
  if (nomNorm === q) return 0;
  if (k === 0) return 1;
  if (nomNorm.includes(' ' + q)) return 2;
  return 3;
}
const alpha = (a, b) => String(a.name ?? '').localeCompare(String(b.name ?? ''), 'fr', { sensitivity: 'base' });

// Clients qui correspondent à la saisie, les plus pertinents d'abord. Saisie vide = tous (alphabétique).
export function propositions(clients, saisie) {
  const q = normClient(saisie);
  return (clients || [])
    .map((c) => ({ c, r: rang(normClient(c.name), q) }))
    .filter((x) => x.r !== null)
    .sort((a, b) => a.r - b.r || alpha(a.c, b.c))
    .map((x) => x.c);
}

// Nom découpé pour l'affichage : [{ t, m }] — m = partie trouvée (surlignée).
export function segments(nom, saisie) {
  const texte = String(nom ?? '');
  const q = normClient(saisie);
  const { norm, carte } = carteNorm(texte);
  const k = q ? norm.indexOf(q) : -1;
  if (k < 0) return [{ t: texte, m: false }];
  const debut = carte[k][0];
  const fin = carte[k + q.length - 1][1];
  return [
    { t: texte.slice(0, debut), m: false },
    { t: texte.slice(debut, fin), m: true },
    { t: texte.slice(fin), m: false },
  ].filter((x) => x.t);
}

// Choix automatique pendant la frappe : la liste s'est réduite à UN client, égal à la saisie.
export function choixAuto(clients, saisie) {
  const q = normClient(saisie);
  if (!q) return null;
  const p = propositions(clients, saisie);
  return p.length === 1 && normClient(p[0].name) === q ? p[0] : null;
}

// Entrée : la proposition mise en avant (index `actif`, la 1re par défaut). Saisie vide → rien
// (jamais « le premier client de l'alphabet » choisi par une Entrée de trop). Si la saisie nomme
// encore le client EN VIGUEUR (`enVigueur`, même absent de la liste : désactivé depuis), Entrée le
// garde — jamais un autre client dont le nom le contient (« Yashi » → « Yashi Pro ») — sauf choix
// explicite d'une autre proposition (↓).
export function choixEntree(clients, saisie, actif = 0, enVigueur = null) {
  if (!normClient(saisie)) return null;
  if (enVigueur && actif === 0 && memeClient(enVigueur, saisie)) return enVigueur;
  const p = propositions(clients, saisie);
  return p[actif] || p[0] || null;
}

// La saisie désigne-t-elle encore le client choisi ? (sinon le choix est retiré : ce qui est
// affiché est toujours ce qui sera utilisé)
export function memeClient(client, saisie) { return !!client && normClient(client.name) === normClient(saisie); }

// Langues du mail (« en,fr » → « anglais puis français »), pour le rappel sous le champ.
export const NOMS_LANGUES = { en: 'anglais', fr: 'français', nl: 'néerlandais' };
export function lireLangues(v) {
  const liste = Array.isArray(v) ? v : String(v ?? '').split(/[\s,;]+/);
  const vues = new Set();
  const res = [];
  for (const x of liste) {
    const l = String(x || '').trim().toLowerCase();
    if (NOMS_LANGUES[l] && !vues.has(l)) { vues.add(l); res.push(l); }
  }
  return res;
}
export const texteLangues = (v) => lireLangues(v).map((l) => NOMS_LANGUES[l]).join(' puis ');

// « Commande de test » réservée aux ADMINISTRATEURS (décision Silviu 17/10, droit commandes_pretes.admin.all
// = `admin` des écrans) : la case n'existe que pour eux — page Commandes prêtes (#cp-test) et écran
// d'encodage (#isTest). Un opérateur encode la commande de test qu'on lui a confiée (badge TEST), sans en
// changer la réf. ni le client, ni la supprimer. Le serveur refuse de toute façon (403 TEST_RESERVE_ADMIN) :
// l'écran ne fait que ne pas proposer.
// Notes de l'écran d'encodage : sans la case, pas un mot sur la case.
export const texteVerrouEntete = (quand, admin) => (admin
  ? `Client et commande de test verrouillés depuis l'envoi du ${quand}.`
  : `Client verrouillé depuis l'envoi du ${quand}.`);
export const texteDejaChargee = (admin) => (admin
  ? 'Des unités sont déjà chargées dans un camion : le client ne change plus, et la commande ne devient pas une commande de test.'
  : 'Des unités sont déjà chargées dans un camion : le client ne change plus.');
// Opérateur sur une commande de test : réf. et client figés pour lui.
export const MSG_ENTETE_TEST_ADMIN = 'Commande de test : la réf. et le client ne se changent que par un administrateur.';
// … et son client manque (ancienne commande, client retiré) : il ne peut pas le choisir, on le lui dit.
export const MSG_CLIENT_MANQUANT_TEST = 'Client manquant : sur une commande de test, seul un administrateur peut le choisir.';
// … et sa réf. est à corriger (ancienne réf. invalide) : même chose pour la réf.
export const MSG_REF_TEST_ADMIN = 'Réf. chargement à corriger : sur une commande de test, seul un administrateur peut la changer.';
// … et c'est son client qui bloque (inactif, plus proposé, sans destinataire…) : il ne peut pas en changer.
export const MSG_CLIENT_TEST_ADMIN = 'sur une commande de test, seul un administrateur peut le corriger (client de la commande ou Paramètres clients).';
// (10e revue) Après le 1er mail, le client de la commande ne change plus pour PERSONNE : seuls les Paramètres clients.
export const MSG_CLIENT_TEST_ADMIN_VERROU = 'seul un administrateur peut le corriger (Paramètres clients).';
export const MSG_CLIENT_MANQUANT_TEST_VERROU = 'Client introuvable : seul un administrateur peut le corriger (Paramètres clients).';
// Client manquant sur une commande de test, sans le droit — UN texte (12e revue) pour #clientManque et la note du
// bouton d'envoi (noteEssaiFige, fiches.js) : `verrouille` = après le 1er mail (seuls les Paramètres clients).
export const texteClientManqueTest = (verrouille) => (verrouille ? MSG_CLIENT_MANQUANT_TEST_VERROU : MSG_CLIENT_MANQUANT_TEST);
// La réf. a aussi une commande de TEST chez ce client (revue 17/10) : le serveur ne crée rien et fait
// choisir (409 CHOIX_TEST_OU_REELLE { choix: { essai: { id, envoyee, creeLe, creePar }, reelleId } }).
// `quand` : creeLe déjà mis en forme (dates.js). Texte de la question + libellé du bouton « vraie commande ».
// (Aucun identifiant n'est requis : la réponse est résolue par le serveur sur la réf. et le client — 7e revue.)
export const lireChoixTest = (d) => (d && d.error === 'CHOIX_TEST_OU_REELLE' && d.choix && d.choix.essai
  && typeof d.choix.essai === 'object' ? d.choix : null);
export function texteChoixTest(choix, quand) {
  const e = choix.essai;
  const qui = e.creePar ? ` par ${e.creePar}` : '';
  const etat = e.envoyee ? 'déjà envoyée à l\'adresse de test' : 'pas encore envoyée';
  return `Cette réf. a aussi une commande de TEST (créée le ${quand}${qui}, ${etat}). Laquelle ouvrir ?`;
}
// La vraie commande existait au moment de la question (`reelleId`) : libellé « Ouvrir » ; sinon « Créer ». Le
// bouton envoie `choix: 'reelle-existante'` (la rouvrir seulement) ou `choix: 'reelle'` (la rouvrir ou la créer) :
// le serveur la retrouve sur cette réf. chez ce client.
export const idVraieCommande = (choix) => (choix && typeof choix.reelleId === 'string' && choix.reelleId ? choix.reelleId : null);
export const libelleVraieCommande = (choix) => (idVraieCommande(choix)
  ? 'Ouvrir la vraie commande (celle du client)'
  : 'Créer la vraie commande — son mail partira au client');
// La question vaut pour la réf. et le client qui l'ont produite (`ref` normalisée, `clientId`, posés par la
// page) : une autre réf. tapée ou scannée entre-temps, ou un autre client → plus de question (null).
export const choixPourSaisie = (choix, ref, clientId) => (choix && typeof choix.ref === 'string' && choix.ref === ref
  && typeof clientId === 'string' && choix.clientId === clientId ? choix : null);

// Rappel des règles du client choisi (sous le champ Client).
export function texteRegles(client, test) {
  if (!client) return '';
  const t = [client.name];
  if (client.carrierCode) t.push(`transporteur ${client.carrierCode}`);
  if (test) t.push('TEST : mail seulement à l\'adresse de test, rien vers EffiTrace');
  else {
    const l = texteLangues(client.mailLanguages);
    if (l) t.push(`mail en ${l}`);
  }
  return t.join(' · ');
}
